import mapboxgl from "mapbox-gl";

// ─── CSS injected once ────────────────────────────────────────────────────────
const KIOSK_STYLE_ID = "__kiosk_marker_styles__";

function injectStyles() {
  if (document.getElementById(KIOSK_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = KIOSK_STYLE_ID;
  style.textContent = `
    /* ── Whole marker drops in from above ── */
    @keyframes kiosk-drop {
      0%   { transform: translateY(-200px) scale(1.08); opacity: 0; }
      55%  { transform: translateY(10px)   scale(0.95); opacity: 1; }
      72%  { transform: translateY(-6px)   scale(1.03); }
      84%  { transform: translateY(3px)    scale(0.99); }
      93%  { transform: translateY(-1px)   scale(1.005); }
      100% { transform: translateY(0px)    scale(1);    opacity: 1; }
    }

    /* ── Ground dot shadow squish synced to drop ── */
    @keyframes kiosk-shadow {
      0%   { transform: scaleX(0.15); opacity: 0; }
      55%  { transform: scaleX(1.4);  opacity: 0.38; }
      72%  { transform: scaleX(0.8);  opacity: 0.22; }
      84%  { transform: scaleX(1.15); opacity: 0.3; }
      100% { transform: scaleX(1);    opacity: 0.2; }
    }

    /* ── Pulse ring on ground dot ── */
    @keyframes kiosk-pulse {
      0%   { transform: translate(-50%, -50%) scale(1);   opacity: 0.65; }
      100% { transform: translate(-50%, -50%) scale(3.4); opacity: 0; }
    }

    /* ── Gentle float on the banner after landing ── */
    @keyframes kiosk-float {
      0%, 100% { transform: translateY(0px); }
      50%       { transform: translateY(-4px); }
    }

    .kiosk-marker-root {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    /* ───── Banner wrap ───── */
    .kiosk-banner-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      animation: kiosk-drop 0.86s cubic-bezier(0.22, 1, 0.36, 1) both;
      transform-origin: bottom center;
    }

    .kiosk-banner-wrap.kiosk-floating {
      animation:
        kiosk-drop 0.86s cubic-bezier(0.22, 1, 0.36, 1) both,
        kiosk-float 3s ease-in-out 1.1s infinite;
    }

    /* ───── Banner pill ───── */
    .kiosk-banner {
      background: #00b878;
      border-radius: 10px;
      padding: 7px 14px 7px 12px;
      display: flex;
      align-items: center;
      gap: 7px;
      box-shadow: 0 6px 20px rgba(0, 140, 90, 0.45), 0 2px 6px rgba(0,0,0,0.2);
      white-space: nowrap;
    }

    .kiosk-banner-text-en {
      font-family: 'DM Sans', 'Segoe UI', system-ui, sans-serif;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.07em;
      color: #ffffff;
      text-transform: uppercase;
      line-height: 1;
    }

    .kiosk-banner-divider {
      width: 1px;
      height: 14px;
      background: rgba(255,255,255,0.35);
      flex-shrink: 0;
    }

    .kiosk-banner-text-ar {
      font-family: 'Tajawal', 'Noto Sans Arabic', sans-serif;
      font-size: 12px;
      font-weight: 700;
      color: rgba(255,255,255,0.92);
      direction: rtl;
      line-height: 1;
    }

    /* ───── Triangle stem (points down from banner) ───── */
    .kiosk-stem {
      width: 0;
      height: 0;
      border-left: 9px solid transparent;
      border-right: 9px solid transparent;
      border-top: 14px solid #00b878;
      filter: drop-shadow(0 3px 3px rgba(0,120,70,0.25));
      flex-shrink: 0;
    }

    /* ───── Thin connector line between stem tip and dot ───── */
    .kiosk-connector {
      width: 2px;
      height: 22px;
      background: linear-gradient(to bottom, rgba(0,184,120,0.65), rgba(0,184,120,0.1));
      flex-shrink: 0;
    }

    /* ───── Ground icon + pulse ───── */
    .kiosk-ground-wrap {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      flex-shrink: 0;
    }

    .kiosk-pulse-ring {
      position: absolute;
      top: 50%; left: 50%;
      width: 32px; height: 32px;
      margin-top: -16px; margin-left: -16px;
      border-radius: 50%;
      background: rgba(0, 184, 120, 0.5);
      animation: kiosk-pulse 2s cubic-bezier(0.2, 0.6, 0.4, 1) 1s infinite;
      pointer-events: none;
    }

    .kiosk-pulse-ring-2 {
      animation-delay: 1.7s;
    }

    .kiosk-dot {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #00b878;
      border: 2.5px solid white;
      box-shadow: 0 2px 10px rgba(0, 140, 90, 0.55);
      position: relative;
      z-index: 1;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .kiosk-dot svg {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
    }

    /* ───── Ground shadow ───── */
    .kiosk-ground-shadow {
      width: 32px;
      height: 7px;
      background: rgba(0,0,0,0.15);
      border-radius: 50%;
      margin-top: 2px;
      filter: blur(2px);
      animation: kiosk-shadow 0.86s cubic-bezier(0.22, 1, 0.36, 1) both;
      transform-origin: center;
    }
  `;
  document.head.appendChild(style);
}

// ─── Arrow SVG (white, pointing down) ────────────────────────────────────────
const ARROW_SVG = `
  <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 4.5V13.5M9 13.5L5 9.5M9 13.5L13 9.5"
      stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

// ─── Public API ───────────────────────────────────────────────────────────────

export interface KioskMarkerOptions {
  /** Show Arabic "أنت هنا" alongside English (default: true) */
  bilingual?: boolean;
  /** Override brand color hex (default: "#00b878") */
  color?: string;
  /** Delay before animation starts in ms (default: 0) */
  delay?: number;
}

/**
 * Creates the animated "You Are Here" kiosk marker.
 *
 * Layout: floating banner → ▼ triangle stem → thin line → pulsing ground dot
 *
 * The whole assembly drops from above with a bounce, then the banner
 * gently floats up and down. Pulse rings radiate from the ground dot.
 *
 * Returns the mapboxgl.Marker — call .remove() to destroy it.
 *
 * Usage:
 *   const marker = createKioskMarker(map, [lon, lat], { bilingual: true });
 */
export function createKioskMarker(
  map: mapboxgl.Map,
  lngLat: [number, number],
  options: KioskMarkerOptions = {}
): mapboxgl.Marker {
  injectStyles();

  const { bilingual = true, color = "#00b878", delay = 0 } = options;
  const isCustomColor = color !== "#00b878";
  const delayMs = `${delay}ms`;
  const floatDelayMs = `${delay + 1100}ms`;

  // ── Root wrapper ────────────────────────────────────────────────────────────
  const root = document.createElement("div");
  root.className = "kiosk-marker-root";

  // ── Banner wrap (banner + stem + connector drop & float together) ────────────
  const bannerWrap = document.createElement("div");
  bannerWrap.className = "kiosk-banner-wrap kiosk-floating";
  if (delay > 0) {
    // Override both animation delays when a custom delay is set
    bannerWrap.style.animationDelay = `${delayMs}, ${floatDelayMs}`;
  }

  // ── Banner pill ─────────────────────────────────────────────────────────────
  const banner = document.createElement("div");
  banner.className = "kiosk-banner";
  if (isCustomColor) {
    banner.style.background = color;
    banner.style.boxShadow = `0 6px 20px ${hexToRgba(color, 0.45)}, 0 2px 6px rgba(0,0,0,0.2)`;
  }

  const textEn = document.createElement("span");
  textEn.className = "kiosk-banner-text-en";
  textEn.textContent = "You are here";
  banner.appendChild(textEn);

  if (bilingual) {
    const divider = document.createElement("div");
    divider.className = "kiosk-banner-divider";

    const textAr = document.createElement("span");
    textAr.className = "kiosk-banner-text-ar";
    textAr.textContent = "أنت هنا";

    banner.appendChild(divider);
    banner.appendChild(textAr);
  }

  // ── Triangle stem ───────────────────────────────────────────────────────────
  const stem = document.createElement("div");
  stem.className = "kiosk-stem";
  if (isCustomColor) stem.style.borderTopColor = color;

  // ── Thin connector line ─────────────────────────────────────────────────────
  const connector = document.createElement("div");
  connector.className = "kiosk-connector";
  if (isCustomColor) {
    connector.style.background =
      `linear-gradient(to bottom, ${hexToRgba(color, 0.65)}, ${hexToRgba(color, 0.1)})`;
  }

  bannerWrap.appendChild(banner);
  bannerWrap.appendChild(stem);
  bannerWrap.appendChild(connector);

  // ── Ground dot + pulse rings ────────────────────────────────────────────────
  const groundWrap = document.createElement("div");
  groundWrap.className = "kiosk-ground-wrap";

  const ring1 = document.createElement("div");
  ring1.className = "kiosk-pulse-ring";
  const ring2 = document.createElement("div");
  ring2.className = "kiosk-pulse-ring kiosk-pulse-ring-2";

  if (isCustomColor) {
    const tint = hexToRgba(color, 0.5);
    ring1.style.background = tint;
    ring2.style.background = tint;
  }

  const dot = document.createElement("div");
  dot.className = "kiosk-dot";
  dot.innerHTML = ARROW_SVG;
  if (isCustomColor) {
    dot.style.background = color;
    dot.style.boxShadow = `0 2px 8px ${hexToRgba(color, 0.5)}`;
  }

  groundWrap.appendChild(ring1);
  groundWrap.appendChild(ring2);
  groundWrap.appendChild(dot);

  // ── Ground shadow ───────────────────────────────────────────────────────────
  const groundShadow = document.createElement("div");
  groundShadow.className = "kiosk-ground-shadow";
  if (delay > 0) groundShadow.style.animationDelay = delayMs;

  // ── Assemble ────────────────────────────────────────────────────────────────
  root.appendChild(bannerWrap);
  root.appendChild(groundWrap);
  root.appendChild(groundShadow);

  // ── Mapbox marker — anchor bottom so dot sits on the coordinate ──────────────
  const marker = new mapboxgl.Marker({
    element: root,
    anchor: "bottom",
    offset: [0, 0],
  })
    .setLngLat(lngLat)
    .addTo(map);

  return marker;
}

// ─── Helper ───────────────────────────────────────────────────────────────────
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

